import React, { useEffect, useState } from "react";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import PromotedImagePreview from "../../../creativePreviews/PromotedImagePreview";
import { proxyDownload } from "../../../utils/proxyDownload"; // 🔁 Adjust path if needed

const RoughDescriptionBox = ({ description }) => {
  if (!description) return null;
  return (
    <div className="bg-gray-100 p-3 rounded-md">
      <pre className="whitespace-pre-wrap break-words font-mono text-gray-700 text-lg">Rough Description: {description}</pre>
    </div>
  );
};

const SideInfoBox = ({ data, currentIndex, images }) => {
  const current = images?.[currentIndex];

  return (
    <div className="w-1/3 flex flex-col gap-4 bg-white rounded-lg shadow-md p-4 overflow-hidden">
      <RoughDescriptionBox description={data.rough_description} />

      {images?.length > 0 && (
        <div className="text-xs text-gray-500">
          Image <span className="font-medium">{currentIndex + 1}</span> of {images.length}
        </div>
      )}

      {current?.type === "sub" && current?.text && (
        <div className="bg-gray-100 p-3 rounded-md font-bold">
          <pre className="whitespace-pre-wrap break-words font-mono">Text: {current.text}</pre>
        </div>
      )}
    </div>
  );
};

const RightImageBox = ({ images, currentIndex, handlePrev, handleNext }) => {
  const current = images[currentIndex];

  return (
    <div className="w-2/3 relative flex items-center justify-center max-h-[70vh] min-h-[70vh] bg-white rounded-lg shadow-md overflow-hidden">
      <button onClick={handlePrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 transition-all z-10" aria-label="Previous Image">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <img src={current?.url} alt={`creative-${currentIndex}`} className="max-w-full max-h-[70vh] object-contain transition-opacity duration-300" />

      <button onClick={handleNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 transition-all z-10" aria-label="Next Image">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div className={`absolute top-2 right-2 w-12 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${current?.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>{current?.type === "main" ? "MAIN" : "SUB"}</div>
    </div>
  );
};

const PromotedImageStoryLayout = ({ data }) => (
  <div className="flex w-full gap-4">
    <div className="w-1/3 flex flex-col gap-4 bg-white rounded-lg shadow-md p-4 overflow-hidden">
      <RoughDescriptionBox description={data.rough_description} />
    </div>
    <div className="w-2/3 bg-white rounded-lg shadow-md p-4">
      <PromotedImagePreview
        preview={{
          img_urls: data.img_urls?.main,
          head_line: data.head_line,
          description: data.description,
          click_url: data.click_url,
          button_enabled: 0,
          name: data.creative_name,
        }}
      />
    </div>
  </div>
);

const ThumbnailPreview = ({ images, currentIndex, setCurrentIndex }) => (
  <div className="flex flex-wrap gap-2 bg-gray-50 border-t p-2 rounded">
    {images.map((imageObj, idx) => (
      <div key={idx} className="relative">
        <img
          src={imageObj.url}
          alt={`thumb-${idx}`}
          onClick={() => setCurrentIndex(idx)}
          className={`w-12 h-12 object-cover rounded cursor-pointer border transition-transform ${idx === currentIndex ? "border-blue-500 scale-105" : "border-gray-300 hover:border-blue-300"}`}
        />
        <div className={`absolute top-0 right-0 px-1 text-xs font-bold text-white rounded-bl ${imageObj.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>{imageObj.type === "main" ? "M" : "S"}</div>
      </div>
    ))}
  </div>
);

const ImagesModal = ({ isOpen, onClose, adData }) => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (adData) {
      const imgUrls = adData.img_urls || {};
      const otherImages = Object.entries(imgUrls)
        .filter(([key]) => key !== "sub")
        .map(([_, value]) => value)
        .filter(Boolean)
        .map((url) => ({ url, type: "main" }));

      const subImages = Array.isArray(imgUrls.sub)
        ? imgUrls.sub.map((obj) => ({
            url: obj.url,
            text: obj.text,
            type: "sub",
          }))
        : [];

      setImages([...otherImages, ...subImages]);
      setCurrentIndex(0);
    }
  }, [adData]);
  const handleDownload = async () => {
    try {
      if(isDownloading) return;
      setIsDownloading(true);
      const imgUrls = adData.img_urls || {};
      const mainImage = imgUrls.main;
      const subImages = imgUrls.sub;
      const classifiedImageUrl = imgUrls.classified;
      const thumbnailUrl = imgUrls.thumbnail;
      let images = [];
      if (mainImage) images.push({ type: "main", url: mainImage, text: null });
      if (subImages) images = [...images, ...subImages.map((obj) => (typeof obj === "string" ? { url: obj, text: null, type: "sub" } : { url: obj.url, text: obj.text, type: "sub" }))];
      if (classifiedImageUrl) images.push({ type: "classified", url: classifiedImageUrl, text: null });
      if (thumbnailUrl) images.push({ type: "thumbnail", url: thumbnailUrl, text: null });
      const filename = adData.creative_name || "download";
      await proxyDownload(images.map((obj)=>obj.url), filename);
    } catch (err) {
      console.error("Download failed", err.message);
      alert("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);

  if (!isOpen) return;

  return (
    <SlDrawer label="Images Preview" style={{ "--size": "70vw" }} placement="end" open={isOpen} onSlAfterHide={onClose}>
      <div className="p-4 max-h-[90vh] overflow-y-auto">
        <h6 className="m-2 px-2 py-1 rounded-lg">{adData.creative_name}</h6>
        <button className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? 'bg-green-500 cursor-progress':'bg-blue-400'}`} onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? "Downloading..." : "Download Images"}
        </button>
        {adData.ad_creative_sub_type === "promoted_image_story" ? (
          <PromotedImageStoryLayout data={adData} />
        ) : images.length > 0 ? (
          <>
            <ThumbnailPreview images={images} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} />
            <div className="flex w-full gap-4">
              <SideInfoBox data={adData} currentIndex={currentIndex} images={images} />
              <RightImageBox images={images} currentIndex={currentIndex} handlePrev={handlePrev} handleNext={handleNext} />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[60vh] bg-gray-100 rounded-lg">
            <p className="text-gray-500">No Images Available</p>
          </div>
        )}
      </div>
    </SlDrawer>
  );
};

export default ImagesModal;
